import React, { PureComponent } from 'react';
import { Flex } from 'app/components/common/Flex';
import { MiniRegulationEditorPanel } from '../measure/MiniRegulationEditorPanel';
import { TvCounts } from './TvCounts';
import {
    CountType,
    FlightDto,
    MeasureId,
    RegulationOrMcdmOnlyDto,
    SlotList,
    TimePeriod,
    TrafficType,
} from 'app/model/CORE';
import AppContext from 'app/common/context/AppContext';
import SlotTable, { SlotColumn, StandardSlotColumn } from 'app/components/table/SlotTable';
import KeyUtils from 'app/common/util/KeyUtils';
import { SortMode } from 'app/common/table/ManagedTable';
import { ExtendedMonitoredTv, RegulationImpact, TvMeteoAlert } from 'app/model/SOL49';
import MeasureUtils from 'app/common/util/MeasureUtils';
import TimeUtils from 'app/common/util/TimeUtils';
import TimePeriodUtils from 'app/common/util/TimePeriodUtils';
import FlightUtils from 'app/common/util/FlightUtils';
import Utils from 'app/common/util/Utils';
import { SlotListSynthesis } from '../measure/SlotListSynthesis';
import './RegulationImpactPanel.less';
import ConditionalRender from '../../../common/components/ConditionalRender';
import FlightTable, { StandardFlightColumn } from '../../table/FlightTable';
import AuthorisationUtils from '../../../common/context/AuthorisationUtils';
import { Col, Row, Tabs } from 'antd';

export const SLOT_LIST_COLUMNS: SlotColumn[] = [
    StandardSlotColumn.SLOT_TIME,
    StandardSlotColumn.SR,
    StandardSlotColumn.ARCID,
    StandardSlotColumn.ADEP,
    StandardSlotColumn.ADES,
    StandardSlotColumn.ETOT,
    StandardSlotColumn.CTOT,
    StandardSlotColumn.ATOT,
    StandardSlotColumn.KIND,
    StandardSlotColumn.RE_LF_XFR,
    StandardSlotColumn.REGS,
    StandardSlotColumn.MPR,
    StandardSlotColumn.DELAY,
];

interface RegulationImpactPanelProps {
    regulation: RegulationOrMcdmOnlyDto;
    regulationImpact: RegulationImpact;
    monitoredTv: ExtendedMonitoredTv;
    meteoAlerts?: Array<TvMeteoAlert>;
    simplifiedDisplay?: boolean;
    onRegulationEdited?: (measureId: MeasureId, regulation: RegulationOrMcdmOnlyDto) => void;
    onApplicableScenarios?: (applicableScenarios: Map<string, number>) => void;
    withoutEditor?: boolean;
    readonly?: boolean;
}

interface RegulationImpactPanelState {
    regulation: RegulationOrMcdmOnlyDto; // Regulation that is being edited
}

export class RegulationImpactPanel extends PureComponent<RegulationImpactPanelProps, RegulationImpactPanelState> {
    constructor(props) {
        super(props);
        this.state = {
            regulation: this.props.regulation,
        };
    }

    componentDidUpdate(prevProps) {
        if (this.props.regulation !== prevProps?.regulation) {
            this.setState({
                regulation: this.props?.regulation,
            });
        }
    }

    handleRegulationUpdated = (regulation: RegulationOrMcdmOnlyDto) => {
        const measureId = this.props.regulation?.measureId;
        this.setState({ regulation: regulation }, () => this.props.onRegulationEdited?.(measureId, regulation));
    };

    handleSelectedPeriodChange = (_countType: CountType, selectedPeriod: TimePeriod) => {
        const flightsWithScenario = this.props.regulationImpact?.flightList
            ?.filter(flight => FlightUtils.hasApplicableScenarios(flight))
            .filter(flight => FlightUtils.isNotDeparted(flight))
            .filter(flight => {
                const tp: TimePeriod = {
                    from: flight.timeAtReferenceLocationEntry.time,
                    to: flight.timeAtReferenceLocationExit.time,
                };
                return TimePeriodUtils.intersects(selectedPeriod, tp);
            });
        const scenarios = Utils.removeDuplicates(
            flightsWithScenario.flatMap(flight => flight.applicableScenarios).flatMap(scenario => scenario.scenarios),
        );
        const applicableScenarios = new Map<string, number>();
        scenarios.forEach(scenario => {
            const nbFlightsConcerned = flightsWithScenario.filter(flight => {
                const scenarios = flight.applicableScenarios?.flatMap(o => o.scenarios);
                return Utils.contains(scenarios, scenario);
            }).length;
            if (nbFlightsConcerned) {
                applicableScenarios.set(scenario, nbFlightsConcerned);
            }
        });

        this.props.onApplicableScenarios?.(applicableScenarios);
    };

    renderSlotListSynthesis = (slotList: SlotList) => {
        if (Utils.isNotExists(slotList)) return undefined;

        return (
            <Flex direction="vertical" className="slot-list-synthesis">
                <Flex className="header">SLOT LIST SYNTHESIS</Flex>
                <SlotListSynthesis slotList={slotList} />
            </Flex>
        );
    };

    render() {
        const regulation = this.state.regulation;
        const regulationImpact = this.props.regulationImpact;
        if (!regulation) return null;

        const tvId = MeasureUtils.getTvLocation(regulation).id;
        const slotList = regulationImpact?.slotList;
        const slots = slotList?.slotSequences
            ?.map(slotSequence => slotSequence.slots)
            .flat()
            .sort((a, b) => {
                return TimeUtils.diffInMs(a.referenceTime, b.referenceTime);
            });
        const regPeriods = MeasureUtils.getRegulationPeriods(regulation);
        const regulationPeriods = regPeriods && [regPeriods];

        let simplifiedDisplayFlightListItems;
        if (this.props.simplifiedDisplay) {
            simplifiedDisplayFlightListItems = [
                {
                    label: 'Flight List Synthesis',
                    key: 'flsyn',
                    children: this.renderFlightListSynthesis(
                        regulationImpact?.flightList?.filter(
                            flight => flight.mostPenalisingRegulation === regulation.measureId.id,
                        ),
                    ),
                },
                {
                    label: 'Flight List',
                    key: 'flist',
                    children: (
                        <Flex className="flight-table">
                            <FlightTable
                                data={regulationImpact?.flightList}
                                columns={[
                                    StandardFlightColumn.ENTRY_TIME,
                                    StandardFlightColumn.ENTRY_FL,
                                    StandardFlightColumn.EXIT_TIME,
                                    StandardFlightColumn.EXIT_FL,
                                    StandardFlightColumn.ARCID,
                                    StandardFlightColumn.ATYPE,
                                    StandardFlightColumn.ADEP,
                                    StandardFlightColumn.ADES,
                                    StandardFlightColumn.IOBT,
                                    StandardFlightColumn.CFMU_TYPE,
                                    StandardFlightColumn.ETOT_CTOT,
                                    StandardFlightColumn.ATOT,
                                    StandardFlightColumn.xTA,
                                    StandardFlightColumn.MPR,
                                    StandardFlightColumn.DELAY,
                                ]}
                                keyProvider={KeyUtils.FLIGHT_KEY_PROVIDER}
                                noDataMessage={'No flights'}
                                defaultSorting={[
                                    {
                                        columnName: StandardFlightColumn.ENTRY_TIME.header,
                                        direction: 'asc',
                                    },
                                ]}
                            />
                        </Flex>
                    ),
                },
            ];
        }

        return (
            <Flex direction="vertical" className="regulation-impact-panel">
                <ConditionalRender condition={!this.props.withoutEditor}>
                    <Flex grow={false} style={{ padding: '1em' }}>
                        <MiniRegulationEditorPanel
                            regulation={regulation}
                            onRegulationUpdated={this.handleRegulationUpdated}
                            readonly={this.props.readonly}
                        />
                    </Flex>
                </ConditionalRender>
                <Flex grow={false} style={{ padding: '1em' }} className={'count-component'}>
                    <TvCounts
                        entryCounts={regulationImpact?.entryCountsComparison?.counts}
                        entryComparisonCounts={regulationImpact?.entryCountsComparison?.comparisonCounts}
                        entryTrafficType={TrafficType.LOAD}
                        entryInterval={20}
                        entryStep={60}
                        occCounts={regulationImpact?.occupancyCountsComparison?.counts}
                        occComparisonCounts={regulationImpact?.occupancyCountsComparison?.comparisonCounts}
                        occTrafficType={TrafficType.LOAD}
                        otmvDuration={regulationImpact?.occupancyCountsComparison?.counts?.request?.durationInMin}
                        loadValues={this.props.monitoredTv?.loadValues}
                        occupancyLoadValues={this.props.monitoredTv?.occupancyLoadValues}
                        regulationPeriods={regulationPeriods}
                        meteoAlerts={this.props.meteoAlerts}
                        currentTime={AppContext.getCurrentTime()}
                        tvId={tvId}
                        tvSet={''}
                        onSelectedPeriodChange={this.handleSelectedPeriodChange}
                    />
                </Flex>
                <ConditionalRender condition={AuthorisationUtils.isNmUser() && AppContext.isNmvpBaseline()}>
                    <Flex direction="vertical" grow={false} className="slot-list">
                        <Flex className="header">SLOT LIST</Flex>
                        <Flex className="slot-table">
                            <SlotTable
                                data={slots}
                                columns={SLOT_LIST_COLUMNS}
                                keyProvider={KeyUtils.SLOT_KEY_PROVIDER}
                                columnResizeDisabled={true}
                                noDataMessage={'No slots'}
                                sortMode={SortMode.NONE}
                            />
                        </Flex>
                    </Flex>
                    {this.renderSlotListSynthesis(slotList)}
                </ConditionalRender>
                <ConditionalRender condition={AuthorisationUtils.isFmpUser() || !AppContext.isNmvpBaseline()}>
                    <Flex direction="vertical" grow={false} className="flight-list">
                        {this.props.simplifiedDisplay && (
                            <Tabs items={simplifiedDisplayFlightListItems} type={'card'} style={{ height: '8em' }} />
                        )}
                        {!this.props.simplifiedDisplay && (
                            <>
                                <Flex className="header">FLIGHT LIST</Flex>
                                <Flex className="flight-table">
                                    <FlightTable
                                        data={regulationImpact?.flightList}
                                        columns={[
                                            StandardFlightColumn.ENTRY_TIME,
                                            StandardFlightColumn.ENTRY_FL,
                                            StandardFlightColumn.EXIT_TIME,
                                            StandardFlightColumn.EXIT_FL,
                                            StandardFlightColumn.ARCID,
                                            StandardFlightColumn.ATYPE,
                                            StandardFlightColumn.ADEP,
                                            StandardFlightColumn.ADES,
                                            StandardFlightColumn.IOBT,
                                            StandardFlightColumn.CFMU_TYPE,
                                            StandardFlightColumn.ETOT_CTOT,
                                            StandardFlightColumn.ATOT,
                                            StandardFlightColumn.xTA,
                                            StandardFlightColumn.MPR,
                                            StandardFlightColumn.DELAY,
                                        ]}
                                        keyProvider={KeyUtils.FLIGHT_KEY_PROVIDER}
                                        noDataMessage={'No flights'}
                                        defaultSorting={[
                                            {
                                                columnName: StandardFlightColumn.ENTRY_TIME.header,
                                                direction: 'asc',
                                            },
                                        ]}
                                    />
                                </Flex>
                                {this.renderFlightListSynthesis(
                                    regulationImpact?.flightList?.filter(
                                        flight => flight.mostPenalisingRegulation === regulation.measureId.id,
                                    ),
                                )}
                            </>
                        )}
                    </Flex>
                </ConditionalRender>
            </Flex>
        );
    }

    private renderFlightListSynthesis(flightList: Array<FlightDto>) {
        if (!flightList) return null;

        const flightDelays = flightList.map(flight => TimeUtils.millescondsToMinutes(flight.delayInMs));
        const nbFlightsDelayed = flightDelays.filter(delay => delay > 0).length;
        const totalDelay = flightDelays.reduce((total, delay) => total + delay, 0);
        const averageDelay = Math.round(nbFlightsDelayed > 0 ? totalDelay / nbFlightsDelayed : 0);
        const maxDelay = Utils.size(flightDelays) > 0 ? Math.max(...flightDelays) : 0;

        return (
            <Flex direction="vertical" className="flight-list-synthesis">
                {!this.props.simplifiedDisplay && <Flex className="header">FLIGHT LIST SYNTHESIS</Flex>}
                <Flex className={'flight-list-synthesis'}>
                    <Flex size={'50%'}>
                        <Row style={{ width: '100%' }}>
                            <Col span={4} className="centered">
                                {flightList.length}
                                <br />
                                <span style={{ fontSize: '0.9em' }}>flights caught</span>
                            </Col>
                            <Col span={4} className="centered">
                                {nbFlightsDelayed}
                                <br />
                                <span style={{ fontSize: '0.9em' }}>flights delayed</span>
                            </Col>
                            <Col span={4} className="centered">
                                {totalDelay}
                                <br />
                                <span style={{ fontSize: '0.9em' }}>total delay (min)</span>
                            </Col>
                            <Col span={4} className="centered">
                                {averageDelay}
                                <br />
                                <span style={{ fontSize: '0.9em' }}>avg delay (min)</span>
                            </Col>
                            <Col span={4} className="centered">
                                {maxDelay}
                                <br />
                                <span style={{ fontSize: '0.9em' }}>max delay (min)</span>
                            </Col>
                        </Row>
                    </Flex>
                </Flex>
            </Flex>
        );
    }
}
